import { useForm, Controller, useWatch } from "react-hook-form";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FilterLayout } from "../layout/FilterLayout";
import Button from "../ui/Button";
import { useEffect, useState } from "react";
import {
  exportResignationData,
  fetchDepartments,
  getSubmittedResignationData,
  fetchReportingManagers,
} from "../../services/exitManagement";
import useLoader from "../../hooks/useLoader";
import useAuthUser from "../../hooks/useAuthUser";

const ManagersFilter = ({ updateFilteredData, fetchResignations }) => {
  const { control, handleSubmit, reset, getValues } = useForm({
    defaultValues: {
      keywords: "",
      department: null,
      reportingManager: null,
      status: "",
      buyout: "",
      resignationDateFrom: null,
      resignationDateTo: null,
      lastWorkingDateFrom: null,
      lastWorkingDateTo: null,
    },
  });

  const [departments, setDepartments] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);
  const { hideLoader, showLoader } = useLoader();
  const { loggedInUser } = useAuthUser();
  const userRole = loggedInUser?.role;
  const isAdminOrHr = userRole === "1" || userRole === "2";

  const resignationDateFrom = useWatch({
    control,
    name: "resignationDateFrom",
  });
  const resignationDateTo = useWatch({ control, name: "resignationDateTo" });
  const lastWorkingDateFrom = useWatch({
    control,
    name: "lastWorkingDateFrom",
  });
  const lastWorkingDateTo = useWatch({ control, name: "lastWorkingDateTo" });

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      department: data.department?._DeptID || null,
      reportingManager: data.reportingManager?._UserID || null,
      keywords: data.keywords || "",
      status: data.status || null,
      buyout: data.buyout || null,
    };
    try {
      showLoader();
      const response = await getSubmittedResignationData(1, payload);
      updateFilteredData(response?.data?.result || []);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      hideLoader();
    }
  };

  const handleClear = () => {
    reset();
    fetchResignations(1);
  };

  const handleExport = async () => {
    try {
      showLoader();
      const exportData = getValues();
      const payload = {
        keywords: exportData.keywords || "",
        department: exportData.department?._DeptID || null,
        reportingManager: exportData.reportingManager?._UserID || null,
        status: exportData.status || null,
        buyout: exportData.buyout || null,
        resignationDateFrom: exportData.resignationDateFrom || null,
        resignationDateTo: exportData.resignationDateTo || null,
        lastWorkingDateFrom: exportData.lastWorkingDateFrom || null,
        lastWorkingDateTo: exportData.lastWorkingDateTo || null,
      };
      const response = await exportResignationData(payload);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "resignation_data.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [deptRes, mgrRes] = await Promise.all([
          fetchDepartments(),
          fetchReportingManagers(),
        ]);
        if (deptRes?.data) setDepartments(deptRes.data.data);
        if (mgrRes?.data) setReportingManagers(mgrRes.data.data);
      } catch (error) {
        console.error("Error loading filter options:", error);
      }
    };

    loadFilterOptions();
  }, []);

  const menuProps = {
    PaperProps: {
      sx: {
        maxHeight: 200,
        width: "auto",
        fontSize: "0.8rem",
        "& .MuiMenuItem-root": {
          fontSize: "0.8rem",
          minHeight: "32px",
          lineHeight: "1.2",
          // paddingY: 0.5,
        },
      },
    },
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    },
  };

  return (
    <FilterLayout>
      <form onSubmit={handleSubmit(onSubmit)} className="compact-form">
        <div className="row gy-2 align-items-center">
          {/* Keywords */}
          <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
            <Controller
              name="keywords"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Employee Name/Code"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    backgroundColor: "white",
                    ".MuiInputBase-input": { padding: "8px 8px" },
                  }}
                />
              )}
            />
          </div>

          {/* Department */}
          <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
            <Controller
              name="department"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "white" }}
                >
                  <InputLabel id="department-label">
                    Select Department
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="department-label"
                    label="Select Department"
                    value={field.value?._DeptID || ""}
                    onChange={(e) => {
                      const selected = departments.find(
                        (dept) => dept._DeptID === e.target.value
                      );
                      field.onChange(selected || null);
                    }}
                    MenuProps={menuProps}
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept._DeptID} value={dept._DeptID}>
                        {dept._DeptName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </div>

          {/* Reporting Manager */}
          {isAdminOrHr && (
            <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
              <Controller
                name="reportingManager"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ backgroundColor: "white" }}
                  >
                    <InputLabel id="manager-label">
                      Reporting Manager
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="manager-label"
                      label="Reporting Manager"
                      value={field.value?._UserID || ""}
                      onChange={(e) => {
                        const selected = reportingManagers.find(
                          (mgr) => mgr._UserID === e.target.value
                        );
                        field.onChange(selected || null);
                      }}
                      MenuProps={menuProps}
                    >
                      {reportingManagers.map((mgr) => {
                        const name = [
                          mgr._FirstName,
                          mgr._MiddleName,
                          mgr._LastName,
                        ]
                          .filter(Boolean)
                          .join(" ");
                        return (
                          <MenuItem key={mgr._UserID} value={mgr._UserID}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}
              />
            </div>
          )}

          {/* Status */}
          <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "white" }}
                >
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    {...field}
                    labelId="status-label"
                    label="Status"
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    MenuProps={menuProps}
                  >
                    <MenuItem value={0}>Applied</MenuItem>
                    <MenuItem value={1}>Withdrawn</MenuItem>
                    <MenuItem value={2}>Accepted By Manager</MenuItem>
                    <MenuItem value={3}>Rejected By Manager</MenuItem>
                    <MenuItem value={4}>
                      Round Table Scheduled By Manager
                    </MenuItem>
                    <MenuItem value={5}>HR Accepted</MenuItem>
                    <MenuItem value={6}>HR Rejected</MenuItem>
                    <MenuItem value={9}>HR Retained</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </div>

          {/* Buyout */}
          <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
            <Controller
              name="buyout"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "white" }}
                >
                  <InputLabel id="buyout-label">Buyout</InputLabel>
                  <Select
                    {...field}
                    labelId="buyout-label"
                    label="Buyout"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </div>

          {/* Resignation Date From */}
          <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
            <Controller
              name="resignationDateFrom"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Resignation Date From"
                  {...field}
                  onChange={field.onChange}
                  value={field.value || null}
                  maxDate={resignationDateTo || undefined}
                  format="dd MMM yyyy"
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      size: "small",
                      sx: { backgroundColor: "white" },
                    },
                  }}
                />
              )}
            />
          </div>

          {/* Resignation Date To */}
          <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
            <Controller
              name="resignationDateTo"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Resignation Date To"
                  {...field}
                  onChange={field.onChange}
                  value={field.value || null}
                  minDate={resignationDateFrom || undefined}
                  format="dd MMM yyyy"
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      size: "small",
                      sx: { backgroundColor: "white" },
                    },
                  }}
                />
              )}
            />
          </div>

          {/* Last Working Date From */}
          <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
            <Controller
              name="lastWorkingDateFrom"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Last Working Date From"
                  {...field}
                  onChange={field.onChange}
                  value={field.value || null}
                  format="dd MMM yyyy"
                  maxDate={lastWorkingDateTo || undefined}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      size: "small",
                      sx: { backgroundColor: "white" },
                    },
                  }}
                />
              )}
            />
          </div>

          {/* Last Working Date To */}
          <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
            <Controller
              name="lastWorkingDateTo"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Last Working Date To"
                  minDate={lastWorkingDateFrom || undefined}
                  {...field}
                  onChange={field.onChange}
                  value={field.value || null}
                  format="dd MMM yyyy"
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      size: "small",
                      sx: { backgroundColor: "white" },
                    },
                  }}
                />
              )}
            />
          </div>

          {/* Buttons */}
          <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
            <Button type="submit" variant="success">
              Filter
            </Button>
            <Button
              type="button"
              variant="dark"
              onClick={handleClear}
              style={{ marginLeft: "5px" }}
            >
              Clear
            </Button>
            {isAdminOrHr && (
              <Button
                type="button"
                variant="dark"
                onClick={handleExport}
                style={{ marginLeft: "5px" }}
              >
                Export
              </Button>
            )}
          </div>
        </div>
      </form>
    </FilterLayout>
  );
};

export default ManagersFilter;
